/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file ServiceModeContext.js
 * @description
 * React/stateful half of the Service Modes feature — loads the restaurant's
 * effective service config (`GET /service/config`) and the merged brand
 * catalog (`GET /service-brands/effective`), caches both in AsyncStorage,
 * and exposes the fail-open resolvers from `app/helpers/serviceMode.js` as a
 * single `useServiceMode()` hook.
 *
 * Both fetches run off the boot path (`InteractionManager.runAfterInteractions`)
 * so they never delay first paint, and any failure (offline, 401, …) simply
 * keeps whatever was last cached (or `null`) — the fail-open resolvers treat
 * `null` identically to "no mode applied", so the service module renders
 * exactly like it always has.
 *
 * `useServiceMode()` is safe to call even when no provider is mounted (e.g.
 * a unit test rendering a screen in isolation) — it returns a static,
 * fail-open default object instead of throwing.
 */
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {InteractionManager} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {apiFetch} from './WorkSheetContext';
import {addServiceBrandOption, createServiceBrand, getManagementScope} from './managementApi';
import {
    DEFAULT_CATALOGS,
    DEFAULT_HOURLY_RATE,
    DEFAULT_VAT_RATE,
    isServiceFeatureDisabledError,
    mergeBrands,
    resolveCatalog,
    resolveFeatureEnabled,
    resolveFieldVisible,
} from './serviceMode';
import {ServiceModuleToast} from './ServiceModuleFeedback';
import i18n from '../../i18n';

const CONFIG_CACHE_PREFIX = '@service_mode_config:';
const BRANDS_CACHE_PREFIX = '@service_mode_brands:';

const ServiceModeContext = createContext(null);

/**
 * Case/whitespace-insensitive comparison key for brand/model/type names —
 * mirrors the backend's own de-duplication so the optimistic local update
 * and the persisted row can never disagree ("Avant" vs "avant").
 * @param {*} value Anything stringifiable.
 * @returns {string} The trimmed, lower-cased key.
 */
const lower = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Unwrap a brand mutation result into the brand row itself. `managementApi`
 * peels the controller's `{brand}` envelope already; this stays tolerant of
 * a raw envelope (an older build, a re-wrapping proxy) so the persisted row
 * — and crucially its `_id` — is always merged back, instead of the brand
 * staying id-less and every later append re-creating it into a 409.
 * @param {*} saved A create/append result.
 * @returns {?object} The brand row (`{name, ...}`), or null when there is none.
 */
const brandRow = (saved) => {
    if (!saved || typeof saved !== 'object') {
        return null;
    }
    const row = (!saved.name && saved.brand && typeof saved.brand === 'object') ? saved.brand : saved;

    return row?.name ? row : null;
};

/**
 * Normalize a brand row's models into the `modelCatalog` shape
 * (`[{name, types}]`). Rows from an older backend — and the bundled
 * `car_database.json` fallback — only carry the legacy `models: [String]`
 * mirror, so it is projected into an empty-typed catalog.
 * @param {?object} brand A brand row (`{name, models?, modelCatalog?, types?}`).
 * @returns {Array<{name: string, types: string[]}>} The normalized catalog.
 */
const normalizeModelCatalog = (brand) => {
    const raw = Array.isArray(brand?.modelCatalog) && brand.modelCatalog.length > 0
        ? brand.modelCatalog
        : (Array.isArray(brand?.models) ? brand.models : []);

    return raw
        .map((entry) => (typeof entry === 'string'
            ? {name: entry.trim(), types: []}
            : {
                name: String(entry?.name || '').trim(),
                types: Array.isArray(entry?.types) ? entry.types.filter(Boolean).map((v) => String(v)) : [],
            }))
        .filter((entry) => entry.name);
};

/**
 * Immutably merge a `{model, type}` option into one brand row — the
 * optimistic half of the add-option mutators, mirroring the backend append:
 * a value already present in any casing is left untouched, a `type` sent
 * with a `model` lands on that model, a `type` sent alone on the
 * brand-level list (it then applies to every model of the brand).
 *
 * `models` is always re-derived from the catalog names so the legacy,
 * denormalized mirror shipped app/admin builds read never drifts.
 * @param {object} brand The brand row to extend.
 * @param {object} [option] `{model?: string, type?: string}`.
 * @returns {object} A new brand row with `models`/`modelCatalog`/`types` refreshed.
 */
const mergeOptionIntoBrand = (brand, {model, type} = {}) => {
    const catalog = normalizeModelCatalog(brand).map((entry) => ({...entry, types: [...entry.types]}));
    const types = Array.isArray(brand?.types) ? brand.types.filter(Boolean).map((v) => String(v)) : [];
    const modelName = String(model || '').trim();
    const typeName = String(type || '').trim();

    if (modelName && !catalog.some((entry) => lower(entry.name) === lower(modelName))) {
        catalog.push({name: modelName, types: []});
    }
    if (typeName) {
        const target = modelName ? catalog.find((entry) => lower(entry.name) === lower(modelName)) : null;
        if (target) {
            if (!target.types.some((value) => lower(value) === lower(typeName))) {
                target.types.push(typeName);
            }
        } else if (!types.some((value) => lower(value) === lower(typeName))) {
            types.push(typeName);
        }
    }

    return {...brand, models: catalog.map((entry) => entry.name), modelCatalog: catalog, types};
};

/**
 * Static fail-open default returned by {@link useServiceMode} when no
 * provider is mounted, or before the first fetch resolves — behaviorally
 * identical to "no service mode applied".
 * @returns {object} A `useServiceMode()`-shaped default.
 */
const buildStaticDefault = () => ({
    config: null,
    serviceMode: null,
    ready: true,
    isFeatureEnabled: (key) => resolveFeatureEnabled(null, key),
    isFieldVisible: (entity, key, surface) => resolveFieldVisible(null, entity, key, surface),
    getCatalog: (name, fallback) => resolveCatalog(null, name, fallback ?? DEFAULT_CATALOGS[name]),
    hourlyRate: DEFAULT_HOURLY_RATE,
    vatRate: DEFAULT_VAT_RATE,
    brands: DEFAULT_CATALOGS.brands,
    addBrandLocal: async () => null,
    addBrandOption: async () => null,
    addModel: async () => null,
    addType: async () => null,
    refreshConfig: async () => {},
    notifyFeatureDisabled: (err) => isServiceFeatureDisabledError(err),
});

const STATIC_DEFAULT = buildStaticDefault();

/**
 * Provider — mount once, near the top of the tree (between
 * `TimeTrackingProvider` and `WorkSheetProvider` in `AppProviders.jsx`), so
 * every service-module screen shares one fetch/cache.
 */
export const ServiceModeProvider = ({children}) => {
    const [config, setConfig] = useState(null);
    const [brands, setBrands] = useState(DEFAULT_CATALOGS.brands);
    const [ready, setReady] = useState(false);
    const restaurantIdRef = useRef('');
    const brandsRef = useRef(DEFAULT_CATALOGS.brands);
    // At most ONE in-flight write per brand name (keyed case-insensitively).
    // Two quick-adds fired back-to-back on a bundled brand would otherwise both
    // see an id-less row and both POST /service-brands — the second answering
    // 409 and rolling the first option away — so the second waits for the first
    // to land (and for its `_id`) before it picks create vs. append.
    const brandWritesRef = useRef(new Map());

    /**
     * Set the brand list through a ref-backed mirror — React state updates
     * are asynchronous, `brandsRef` is not, so an optimistic mutation can be
     * rolled back deterministically even when two adds land in one tick.
     * @param {Array<object>|function(Array<object>): Array<object>} updater Next list, or a `prev => next` updater.
     * @returns {Array<object>} The list that was applied.
     */
    const applyBrands = useCallback((updater) => {
        const next = typeof updater === 'function' ? updater(brandsRef.current) : updater;
        brandsRef.current = next;
        setBrands(next);

        return next;
    }, []);

    /**
     * Mirror the brand list into AsyncStorage so the next cold start hydrates
     * the user's own additions before the network answers.
     * @param {Array<object>} list The list to cache.
     * @returns {void}
     */
    const cacheBrands = useCallback((list) => {
        const key = `${BRANDS_CACHE_PREFIX}${restaurantIdRef.current || 'default'}`;
        AsyncStorage.setItem(key, JSON.stringify(Array.isArray(list) ? list : [])).catch(() => {});
    }, []);

    /**
     * Undo ONE failed optimistic brand mutation without clobbering anything
     * that landed meanwhile. Restoring the whole captured list would let the
     * loser of two overlapping quick-adds — or a `/service-brands/effective`
     * refresh finishing mid-flight — wipe the winner, so only the single row
     * that was touched is reverted (or dropped, when it was brand new).
     * @param {string} name Name of the brand that was optimistically mutated.
     * @param {?object} previous The row exactly as it was before the mutation, null when it did not exist yet.
     * @returns {void}
     */
    const rollbackBrand = useCallback((name, previous) => {
        const key = lower(name);
        if (!key) {
            return;
        }
        cacheBrands(applyBrands((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            if (!previous) {
                return list.filter((entry) => lower(entry?.name || entry?.brand) !== key);
            }

            return list.map((entry) => (lower(entry?.name || entry?.brand) === key ? previous : entry));
        }));
    }, [applyBrands, cacheBrands]);

    /**
     * Serialize the persist calls of ONE brand: `run` starts only once the
     * brand's previous write settled, so a second quick-add can see the `_id`
     * the first one brought back and append to that row instead of creating
     * the brand a second time (which answers 409 and rolls the first option
     * away). Writes to different brands still run in parallel, and the
     * optimistic local update stays immediate — only the network call queues.
     * @param {string} name The brand the write belongs to (case-insensitive key).
     * @param {function(): Promise<*>} run Starts the actual request; called with no arguments.
     * @returns {Promise<*>} Whatever `run` resolves with.
     */
    const queueBrandWrite = useCallback((name, run) => {
        const key = lower(name);
        const queued = (brandWritesRef.current.get(key) || Promise.resolve())
            .catch(() => {})
            .then(run);

        brandWritesRef.current.set(key, queued);

        return queued;
    }, []);

    const loadConfig = useCallback(async () => {
        const scope = await getManagementScope().catch(() => null);
        const restaurantId = scope?.restaurantId || '';
        restaurantIdRef.current = restaurantId;
        const cacheKey = `${CONFIG_CACHE_PREFIX}${restaurantId || 'default'}`;
        const brandsCacheKey = `${BRANDS_CACHE_PREFIX}${restaurantId || 'default'}`;

        // 1) Hydrate from cache first so a cold start never flashes "everything
        // enabled" then "some things disabled" — fail-open either way.
        try {
            const cachedRaw = await AsyncStorage.getItem(cacheKey);
            if (cachedRaw) {
                setConfig(JSON.parse(cachedRaw));
            }
        } catch (e) { /* corrupt cache → ignore, keep null (fail-open) */ }
        try {
            const cachedBrandsRaw = await AsyncStorage.getItem(brandsCacheKey);
            if (cachedBrandsRaw) {
                const parsed = JSON.parse(cachedBrandsRaw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    applyBrands(mergeBrands(parsed, DEFAULT_CATALOGS.brands));
                }
            }
        } catch (e) { /* corrupt cache → ignore, keep bundled defaults */ }

        // 2) Network refresh — failures silently keep the cache/defaults.
        try {
            const res = await apiFetch('/service/config');
            const nextConfig = res?.data || res;
            if (nextConfig && typeof nextConfig === 'object') {
                setConfig(nextConfig);
                AsyncStorage.setItem(cacheKey, JSON.stringify(nextConfig)).catch(() => {});
            }
        } catch (e) { /* offline/401/... → keep cached/null config (fail-open) */ }

        try {
            const res = await apiFetch('/service-brands/effective');
            const remoteBrands = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            if (remoteBrands.length > 0) {
                const merged = mergeBrands(remoteBrands, DEFAULT_CATALOGS.brands);
                applyBrands(merged);
                AsyncStorage.setItem(brandsCacheKey, JSON.stringify(remoteBrands)).catch(() => {});
            }
        } catch (e) { /* offline/401/... → keep cached/bundled brands (fail-open) */ }

        setReady(true);
    }, [applyBrands]);

    useEffect(() => {
        const task = InteractionManager.runAfterInteractions(() => {
            loadConfig();
        });
        return () => task?.cancel?.();
    }, [loadConfig]);

    const isFeatureEnabled = useCallback((key) => resolveFeatureEnabled(config, key), [config]);
    const isFieldVisible = useCallback(
        (entity, key, surface = 'form') => resolveFieldVisible(config, entity, key, surface),
        [config],
    );
    const getCatalog = useCallback(
        (name, fallback) => resolveCatalog(config, name, fallback ?? DEFAULT_CATALOGS[name]),
        [config],
    );

    /**
     * Show a warning toast + refresh the config when `err` is a
     * `SERVICE_FEATURE_DISABLED` 403 — call this FIRST in the catch block of
     * any user-triggered service-module mutation. Returns `true` when it
     * handled the error (caller should skip its own generic error toast).
     * @param {*} err The caught error.
     * @returns {boolean} True when this was a disabled-feature 403.
     */
    const notifyFeatureDisabled = useCallback((err) => {
        if (!isServiceFeatureDisabledError(err)) {
            return false;
        }
        ServiceModuleToast.error(
            i18n.t('repair_feature_disabled', {defaultValue: 'Ez a funkció ki van kapcsolva ebben a szerviz módban'}),
        );
        loadConfig();
        return true;
    }, [loadConfig]);

    /**
     * Report a failed brand mutation — a disabled-feature 403 gets the
     * standard service-module toast (and a config refresh), anything else a
     * generic one. Never throws.
     * @param {*} err The caught error.
     * @param {string} key i18n key of the generic message.
     * @param {string} fallbackText Hungarian default for `key`.
     * @returns {void}
     */
    const reportBrandError = useCallback((err, key, fallbackText) => {
        if (notifyFeatureDisabled(err)) {
            return;
        }
        ServiceModuleToast.error(err?.message || i18n.t(key, {defaultValue: fallbackText}));
    }, [notifyFeatureDisabled]);

    /**
     * Add a brand to the catalog — optimistic locally **and persisted** via
     * `POST /service-brands`. Until now this was a local-only mutation, so a
     * brand added on the phone was silently lost on the next reload; a
     * failed save now rolls the optimistic entry back instead.
     * @param {object} brand `{name, models?, modelCatalog?, types?, source?}`.
     * @returns {Promise<?object>} The persisted row (or the optimistic one), null when the save failed.
     */
    const addBrandLocal = useCallback((brand) => {
        const name = String(brand?.name || '').trim();
        if (!name) {
            return Promise.resolve(null);
        }
        const previous = brandsRef.current.find((entry) => lower(entry?.name || entry?.brand) === lower(name)) || null;
        const optimistic = mergeOptionIntoBrand({...brand, name, source: brand?.source || 'user'});
        cacheBrands(applyBrands((prev) => mergeBrands([optimistic], prev)));

        return queueBrandWrite(name, () => createServiceBrand({
            name,
            models: optimistic.models,
            modelCatalog: optimistic.modelCatalog,
            types: optimistic.types,
        })).then((created) => {
            const saved = brandRow(created);
            if (saved) {
                cacheBrands(applyBrands((prev) => mergeBrands([saved], prev)));
            }

            return saved || optimistic;
        }).catch((err) => {
            rollbackBrand(name, previous);
            reportBrandError(err, 'svcmode_brand_add_failed', 'Nem sikerült hozzáadni a márkát.');

            return null;
        });
    }, [applyBrands, cacheBrands, queueBrandWrite, rollbackBrand, reportBrandError]);

    /**
     * Append a model and/or a type to a brand — optimistic locally, then
     * persisted via `POST /service-brands/{id}/options`. A brand that only
     * exists in the bundled fallback has no row id yet, so it is created on
     * the spot carrying the new option (the backend forks `preset`/`ai` rows
     * into `user` rows by itself). Rolls back on failure.
     * @param {string} brandName The brand the option belongs to.
     * @param {object} [option] `{model?: string, type?: string}` — at least one must be non-blank.
     * @returns {Promise<?object>} The persisted brand row, null when the save failed.
     */
    const addBrandOption = useCallback((brandName, {model, type} = {}) => {
        const name = String(brandName || '').trim();
        const modelName = String(model || '').trim();
        const typeName = String(type || '').trim();
        if (!name || (!modelName && !typeName)) {
            return Promise.resolve(null);
        }
        const existing = brandsRef.current.find((entry) => lower(entry?.name || entry?.brand) === lower(name));
        const merged = mergeOptionIntoBrand(
            {...(existing || {source: 'user'}), name: existing?.name || existing?.brand || name},
            {model: modelName, type: typeName},
        );
        cacheBrands(applyBrands((prev) => mergeBrands([merged], prev)));

        // The row id is resolved INSIDE the queued run, not here: an earlier
        // quick-add on the same brand may still be creating it, and by the time
        // this one starts that `_id` is already merged back into `brandsRef`.
        const request = queueBrandWrite(merged.name, () => {
            const current = brandsRef.current.find((entry) => lower(entry?.name || entry?.brand) === lower(name));
            const brandId = current?._id || current?.id;

            return brandId
                ? addServiceBrandOption(brandId, {model: modelName || undefined, type: typeName || undefined})
                : createServiceBrand({
                    name: merged.name,
                    models: merged.models,
                    modelCatalog: merged.modelCatalog,
                    types: merged.types,
                });
        });

        return request.then((result) => {
            // Merging the persisted row back is what gives the brand its `_id`;
            // without it the NEXT quick-add on the same brand would create it a
            // second time and take a 409, rolling the first option away again.
            const saved = brandRow(result);
            if (saved) {
                cacheBrands(applyBrands((prev) => mergeBrands([saved], prev)));
            }

            return saved || merged;
        }).catch((err) => {
            rollbackBrand(merged.name, existing || null);
            reportBrandError(err, 'svcmode_brand_option_failed', 'Nem sikerült menteni a modellt vagy típust.');

            return null;
        });
    }, [applyBrands, cacheBrands, queueBrandWrite, rollbackBrand, reportBrandError]);

    /**
     * Persist a new model under a brand — see {@link addBrandOption}.
     * @param {string} brandName The brand name.
     * @param {string} modelName The model to add.
     * @returns {Promise<?object>} The persisted brand row, null on failure.
     */
    const addModel = useCallback(
        (brandName, modelName) => addBrandOption(brandName, {model: modelName}),
        [addBrandOption],
    );

    /**
     * Persist a new vehicle type/variant — under `modelName` when one is
     * given, otherwise on the brand level (applies to every model).
     * @param {string} brandName The brand name.
     * @param {?string} modelName The model the type belongs to, or falsy for brand-level.
     * @param {string} typeName The type to add.
     * @returns {Promise<?object>} The persisted brand row, null on failure.
     */
    const addType = useCallback(
        (brandName, modelName, typeName) => addBrandOption(brandName, {model: modelName, type: typeName}),
        [addBrandOption],
    );

    const value = useMemo(() => ({
        config,
        serviceMode: config?.serviceMode || null,
        ready,
        isFeatureEnabled,
        isFieldVisible,
        getCatalog,
        hourlyRate: resolveCatalog(config, 'hourlyRate', DEFAULT_HOURLY_RATE),
        vatRate: resolveCatalog(config, 'vatRate', DEFAULT_VAT_RATE),
        brands,
        addBrandLocal,
        addBrandOption,
        addModel,
        addType,
        refreshConfig: loadConfig,
        notifyFeatureDisabled,
    }), [
        config,
        ready,
        isFeatureEnabled,
        isFieldVisible,
        getCatalog,
        brands,
        addBrandLocal,
        addBrandOption,
        addModel,
        addType,
        loadConfig,
        notifyFeatureDisabled,
    ]);

    return (
        <ServiceModeContext.Provider value={value}>
            {children}
        </ServiceModeContext.Provider>
    );
};

/**
 * Read the service-mode state. **Never throws** — returns a static,
 * fail-open default when no `ServiceModeProvider` is mounted, so a screen
 * rendered in isolation (tests, storybook) behaves like "no mode applied".
 * @returns {object} `{config, serviceMode, ready, isFeatureEnabled, isFieldVisible, getCatalog, hourlyRate, vatRate, brands, addBrandLocal, addBrandOption, addModel, addType, refreshConfig, notifyFeatureDisabled}`
 */
export const useServiceMode = () => {
    const ctx = useContext(ServiceModeContext);
    return ctx || STATIC_DEFAULT;
};

export default ServiceModeContext;
